interface PublicSeoEcommerceSettings {
  hero_title?: string | null;
  hero_cover_url?: string | null;
}

export interface PublicSeoShop {
  name?: string | null;
  slogan?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  currency_code?: string | null;
  logo_url?: string | null;
  ecommerce?: PublicSeoEcommerceSettings | null;
}

export interface PublicSeoProduct {
  id?: number | string | null;
  name?: string | null;
  slug?: string | null;
  sku?: string | null;
  description?: string | null;
  price?: number | string | null;
  sale_price?: number | string | null;
  stock_quantity?: number | null;
  is_in_stock?: boolean | null;
  image?: string | null;
  image_url?: string | null;
  main_photo_url?: string | null;
  thumbnail_url?: string | null;
  images?: string[] | null;
  category_name?: string | null;
  rayon_name?: string | null;
}

export interface PublicSeoBuildOptions {
  image?: string | null;
  origin?: string;
  robots?: string;
  url?: string;
}

interface PublicSeoMetaDefinition {
  name?: string;
  property?: string;
  content: string;
}

export interface PublicSeoPayload {
  title: string;
  description: string;
  canonicalUrl?: string | null;
  image?: string | null;
  locale?: string;
  robots?: string;
  siteName?: string;
  structuredData?: Record<string, unknown> | null;
  type?: string;
  extraMeta?: PublicSeoMetaDefinition[];
}

const APP_TITLE = 'Suite241 Gestion Commerciale';
const DEFAULT_SHOP_NAME = 'Suite241 Gestion Commerciale';
const DEFAULT_PUBLIC_DESCRIPTION = 'Découvrez les produits disponibles, comparez les prix et préparez votre commande en ligne.';
const DEFAULT_APP_DESCRIPTION = 'Suite241 Gestion Commerciale, stocks, ventes, achats, caisse, clients et rapports.';
const MANAGED_META_ATTRIBUTE = 'data-suite241-public-seo';
const EXTRA_META_ATTRIBUTE = 'data-suite241-public-seo-extra';
const STRUCTURED_DATA_ID = 'suite241-public-seo-jsonld';

const cleanString = (value: unknown): string => String(value || '').replace(/\s+/g, ' ').trim();

const stripHtml = (value: unknown): string => cleanString(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const truncateText = (value: unknown, maxLength = 160): string => {
  const text = cleanString(value);

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength - 3).trim()}...`;
};

const browserOrigin = (): string => {
  if (typeof window === 'undefined') {
    return 'https://suite241.com';
  }

  return window.location.origin;
};

const currentCanonicalUrl = (origin = browserOrigin()): string => {
  if (typeof window === 'undefined') {
    return origin;
  }

  return new URL(window.location.pathname || '/', origin).toString();
};

export const toAbsolutePublicUrl = (value?: string | null, origin = browserOrigin()): string | null => {
  const url = cleanString(value);

  if (!url) {
    return null;
  }

  try {
    return new URL(url, origin).toString();
  } catch {
    return url;
  }
};

const shopName = (shop?: PublicSeoShop | null): string => cleanString(shop?.name) || DEFAULT_SHOP_NAME;

const currencyCode = (shop?: PublicSeoShop | null): string => cleanString(shop?.currency_code) || 'XAF';

const seoPrice = (product?: PublicSeoProduct | null): number | null => {
  const salePrice = Number(product?.sale_price || 0);
  const regularPrice = Number(product?.price || 0);
  const price = salePrice > 0 ? salePrice : regularPrice;

  return Number.isFinite(price) && price > 0 ? price : null;
};

const seoPriceLabel = (amount: number | null, currency: string): string => {
  if (!amount) {
    return '';
  }

  return `${amount.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} ${currency}`;
};

const productImage = (product?: PublicSeoProduct | null): string | null => {
  if (Array.isArray(product?.images) && product?.images?.length) {
    return product.images[0] || null;
  }

  return cleanString(product?.image)
    || cleanString(product?.image_url)
    || cleanString(product?.main_photo_url)
    || cleanString(product?.thumbnail_url)
    || null;
};

const removeEmptyValues = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    const items = value
      .map((item) => removeEmptyValues(item))
      .filter((item) => item !== null && item !== undefined && item !== '');

    return items.length ? items : undefined;
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .map(([key, entryValue]) => [key, removeEmptyValues(entryValue)] as [string, unknown])
      .filter(([, entryValue]) => entryValue !== null && entryValue !== undefined && entryValue !== '');

    return entries.length ? Object.fromEntries(entries) : undefined;
  }

  return value;
};

export const buildPublicStorefrontSeo = (
  shop?: PublicSeoShop | null,
  options: PublicSeoBuildOptions = {}
): PublicSeoPayload => {
  const name = shopName(shop);
  const heroTitle = cleanString(shop?.ecommerce?.hero_title);
  const slogan = cleanString(shop?.slogan);
  const title = heroTitle ? `${heroTitle} | ${name}` : `${name} - Boutique en ligne`;
  const description = truncateText(
    slogan || `Découvrez le catalogue de ${name}, les produits disponibles et préparez votre commande en ligne.`
  );
  const canonicalUrl = options.url || currentCanonicalUrl(options.origin);
  const image = toAbsolutePublicUrl(options.image || shop?.ecommerce?.hero_cover_url || shop?.logo_url, options.origin);
  const structuredData = removeEmptyValues({
    '@context': 'https://schema.org',
    '@type': 'Store',
    name,
    description,
    url: canonicalUrl,
    image,
    address: {
      '@type': 'PostalAddress',
      streetAddress: cleanString(shop?.address),
      addressLocality: cleanString(shop?.city),
      addressCountry: cleanString(shop?.country),
    },
  }) as Record<string, unknown> | undefined;

  return {
    title: truncateText(title, 70),
    description,
    canonicalUrl,
    image,
    robots: options.robots || 'index,follow',
    siteName: name,
    structuredData: structuredData || null,
    type: 'website',
  };
};

export const buildPublicCheckoutSeo = (
  shop?: PublicSeoShop | null,
  options: PublicSeoBuildOptions = {}
): PublicSeoPayload => {
  const name = shopName(shop);
  const canonicalUrl = options.url || currentCanonicalUrl(options.origin);

  return {
    title: `Préparer la commande | ${name}`,
    description: truncateText(`Vérifiez votre panier et envoyez votre demande de commande à ${name}.`),
    canonicalUrl,
    image: toAbsolutePublicUrl(options.image || shop?.logo_url || shop?.ecommerce?.hero_cover_url, options.origin),
    robots: options.robots || 'noindex,follow',
    siteName: name,
    type: 'website',
  };
};

export const buildPublicShopsSeo = (
  shop?: PublicSeoShop | null,
  options: PublicSeoBuildOptions = {}
): PublicSeoPayload => {
  const name = shopName(shop);
  const canonicalUrl = options.url || currentCanonicalUrl(options.origin);
  const description = truncateText(`Retrouvez les boutiques de ${name}, leurs coordonnées et leurs catalogues publics.`);
  const image = toAbsolutePublicUrl(options.image || shop?.logo_url || shop?.ecommerce?.hero_cover_url, options.origin);
  const structuredData = removeEmptyValues({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url: canonicalUrl,
    logo: toAbsolutePublicUrl(shop?.logo_url, options.origin),
    image,
  }) as Record<string, unknown> | undefined;

  return {
    title: `Nos boutiques | ${name}`,
    description,
    canonicalUrl,
    image,
    robots: options.robots || 'index,follow',
    siteName: name,
    structuredData: structuredData || null,
    type: 'website',
  };
};

export const buildPublicContactSeo = (
  shop?: PublicSeoShop | null,
  options: PublicSeoBuildOptions = {}
): PublicSeoPayload => {
  const name = shopName(shop);
  const canonicalUrl = options.url || currentCanonicalUrl(options.origin);

  return {
    title: `Contact | ${name}`,
    description: truncateText(`Contactez ${name} pour une question, une disponibilité produit ou une information boutique.`),
    canonicalUrl,
    image: toAbsolutePublicUrl(options.image || shop?.logo_url || shop?.ecommerce?.hero_cover_url, options.origin),
    robots: options.robots || 'index,follow',
    siteName: name,
    type: 'website',
  };
};

export const buildPublicProductSeo = (
  shop?: PublicSeoShop | null,
  product?: PublicSeoProduct | null,
  options: PublicSeoBuildOptions = {}
): PublicSeoPayload => {
  const name = shopName(shop);
  const canonicalUrl = options.url || currentCanonicalUrl(options.origin);

  if (!product) {
    return {
      title: `Produit | ${name}`,
      description: truncateText(`Consultez le détail du produit dans le catalogue public de ${name}.`),
      canonicalUrl,
      image: toAbsolutePublicUrl(options.image || shop?.logo_url || shop?.ecommerce?.hero_cover_url, options.origin),
      robots: options.robots || 'index,follow',
      siteName: name,
      type: 'product',
    };
  }

  const productName = cleanString(product.name) || 'Produit';
  const currency = currencyCode(shop);
  const amount = seoPrice(product);
  const priceLabel = seoPriceLabel(amount, currency);
  const category = cleanString(product.category_name) || cleanString(product.rayon_name);
  const description = truncateText(
    stripHtml(product.description)
      || `${productName} disponible chez ${name}${category ? ` dans ${category}` : ''}${priceLabel ? ` au prix de ${priceLabel}` : ''}.`
  );
  const image = toAbsolutePublicUrl(options.image || productImage(product) || shop?.logo_url, options.origin);
  const availability = product.is_in_stock === false ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock';
  const structuredData = removeEmptyValues({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: productName,
    description,
    image: image ? [image] : undefined,
    sku: cleanString(product.sku),
    category,
    offers: {
      '@type': 'Offer',
      url: canonicalUrl,
      priceCurrency: currency,
      price: amount ? amount.toFixed(2) : undefined,
      availability,
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        name,
      },
    },
  }) as Record<string, unknown> | undefined;
  const extraMeta: PublicSeoMetaDefinition[] = [];

  if (amount) {
    extraMeta.push(
      { property: 'product:price:amount', content: amount.toFixed(2) },
      { property: 'product:price:currency', content: currency }
    );
  }

  extraMeta.push({
    property: 'product:availability',
    content: product.is_in_stock === false ? 'out of stock' : 'in stock',
  });

  return {
    title: truncateText(`${productName}${priceLabel ? ` - ${priceLabel}` : ''} | ${name}`, 70),
    description,
    canonicalUrl,
    image,
    robots: options.robots || 'index,follow',
    siteName: name,
    structuredData: structuredData || null,
    type: 'product',
    extraMeta,
  };
};

const setMetaTag = (
  attributeName: 'name' | 'property',
  attributeValue: string,
  content?: string | null,
  isExtra = false
): void => {
  const selector = `meta[${attributeName}="${attributeValue}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);

  if (!content) {
    if (tag?.hasAttribute(MANAGED_META_ATTRIBUTE) || tag?.hasAttribute(EXTRA_META_ATTRIBUTE)) {
      tag.remove();
    }
    return;
  }

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attributeName, attributeValue);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
  tag.setAttribute(MANAGED_META_ATTRIBUTE, 'true');

  if (isExtra) {
    tag.setAttribute(EXTRA_META_ATTRIBUTE, 'true');
  } else {
    tag.removeAttribute(EXTRA_META_ATTRIBUTE);
  }
};

const setCanonicalTag = (href?: string | null): void => {
  let tag = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!href) {
    if (tag?.hasAttribute(MANAGED_META_ATTRIBUTE)) {
      tag.remove();
    }
    return;
  }

  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', 'canonical');
    document.head.appendChild(tag);
  }

  tag.setAttribute('href', href);
  tag.setAttribute(MANAGED_META_ATTRIBUTE, 'true');
};

const setStructuredData = (structuredData?: Record<string, unknown> | null): void => {
  let tag = document.getElementById(STRUCTURED_DATA_ID) as HTMLScriptElement | null;

  if (!structuredData) {
    tag?.remove();
    return;
  }

  if (!tag) {
    tag = document.createElement('script');
    tag.id = STRUCTURED_DATA_ID;
    tag.type = 'application/ld+json';
    document.head.appendChild(tag);
  }

  tag.textContent = JSON.stringify(structuredData);
};

const removeExtraSeoMeta = (): void => {
  document.head
    .querySelectorAll<HTMLMetaElement>(`meta[${EXTRA_META_ATTRIBUTE}="true"]`)
    .forEach((tag) => tag.remove());
};

export const applyPublicSeo = (seo: PublicSeoPayload): void => {
  if (typeof document === 'undefined') {
    return;
  }

  document.title = seo.title || APP_TITLE;
  removeExtraSeoMeta();

  setMetaTag('name', 'description', seo.description);
  setMetaTag('name', 'robots', seo.robots || 'index,follow');
  setMetaTag('property', 'og:title', seo.title);
  setMetaTag('property', 'og:description', seo.description);
  setMetaTag('property', 'og:type', seo.type || 'website');
  setMetaTag('property', 'og:url', seo.canonicalUrl || null);
  setMetaTag('property', 'og:image', seo.image || null);
  setMetaTag('property', 'og:site_name', seo.siteName || DEFAULT_SHOP_NAME);
  setMetaTag('property', 'og:locale', seo.locale || 'fr_FR');
  setMetaTag('name', 'twitter:card', seo.image ? 'summary_large_image' : 'summary');
  setMetaTag('name', 'twitter:title', seo.title);
  setMetaTag('name', 'twitter:description', seo.description);
  setMetaTag('name', 'twitter:image', seo.image || null);

  (seo.extraMeta || []).forEach((meta) => {
    if (meta.name) {
      setMetaTag('name', meta.name, meta.content, true);
    }

    if (meta.property) {
      setMetaTag('property', meta.property, meta.content, true);
    }
  });

  setCanonicalTag(seo.canonicalUrl || null);
  setStructuredData(seo.structuredData || null);
};

export const applyDefaultAppSeo = (): void => {
  applyPublicSeo({
    title: APP_TITLE,
    description: DEFAULT_APP_DESCRIPTION,
    robots: 'noindex,nofollow',
    siteName: APP_TITLE,
    type: 'website',
  });
};

export const buildDefaultPublicSeo = (): PublicSeoPayload => ({
  title: `${DEFAULT_SHOP_NAME} - Boutique en ligne`,
  description: DEFAULT_PUBLIC_DESCRIPTION,
  canonicalUrl: currentCanonicalUrl(),
  robots: 'index,follow',
  siteName: DEFAULT_SHOP_NAME,
  type: 'website',
});
